
import type { Promotion } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { AuditItem, PromotionsMessages } from '../../../../models'
import type { AuditItemDetailsMessages } from './audit-item-details.component.vue'
import { AdapterBooleanUtils } from '@empathyco/playboard-core-domain'
import { defineComponent, getCurrentInstance } from 'vue'
import { usePivotableAudit } from '../../../../composables/use-pivotable-audit'
import AuditItemDetails from './audit-item-details.component.vue'

type PromotionAudit = Omit<Promotion, 'criteria'> & {
  criteria: {
    matchAll: string
    pivots: Record<string, string>
    queries: string
    enabled: string
  }
}

export default defineComponent({
  name: 'PromotionAuditItemDetails',
  components: {
    AuditItemDetails,
  },
  props: {
    item: {
      type: Object as PropType<AuditItem<Promotion>>,
      required: true,
    },
    messages: {
      type: Object as PropType<AuditItemDetailsMessages>,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()
    const { transform: transformPivots } = usePivotableAudit()

    const toolMessages: PromotionsMessages = $epb.messages.tools.promotions

    const transformedMessages: AuditItemDetailsMessages = {
      ...props.messages,
      fields: {
        ...props.messages.fields,
        'metadata.created': toolMessages.fields.creationDate.label,
        'metadata.updated': toolMessages.fields.updateDate.label,
        'metadata.author': toolMessages.fields.user.label,
        'criteria.keyword': toolMessages.fields.keywords.label,
      },
    }

    const transformedItem: AuditItem<PromotionAudit> = {
      ...props.item,
      payload: {
        ...props.item?.payload,
        previousState: transformState(props.item?.payload?.previousState),
        nextState: transformState(props.item?.payload?.nextState),
      },
    }

    function transformState(item: Promotion): PromotionAudit | undefined {
      if (!item) {
        return
      }

      const { pivots } = transformPivots(item.criteria)

      return {
        ...item,
        criteria: {
          ...item.criteria,
          enabled: AdapterBooleanUtils.toBoolean(item.criteria.enabled)
            ? toolMessages.fields.enabled.states.enabled
            : toolMessages.fields.enabled.states.disabled,
          matchAll: AdapterBooleanUtils.toBoolean(item.criteria.matchAll)
            ? toolMessages.fields.matchAll.enabled
            : toolMessages.fields.matchAll.disabled,
          pivots,
          queries: item.criteria.queries.join(', '),
        },
      }
    }

    return {
      baseClass: 'promotion-audit-item-details',
      transformedMessages,
      transformedItem,
    }
  },
})
